import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Briefcase, Wallet, Package, Headset, TrendingUp, Settings, Grip, KeyRound,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './Button';
import { useRbacStore } from '../store/rbacStore';

type AppItem = {
  title: string;
  icon: React.ElementType;
  path: string;
  /** Hub key for access control; omitted for always-visible entries (Dashboard). */
  key?: string;
  /** Idle icon-tile classes (a distinct colour per module). */
  tile: string;
  /** Icon-tile classes when this is the current module (solid fill). */
  activeTile: string;
};

/**
 * Mirrors the real app modules in Sidebar.tsx so every app is reachable.
 * Each module has its own colour (a deliberate, product-owner-requested
 * departure from the slate-and-blue launcher) so the grid reads at a glance.
 */
const APPS: AppItem[] = [
  { title: 'Dashboard', icon: LayoutDashboard, path: '/dashboard',
    tile: 'border-indigo-500/20 bg-indigo-500/10 text-indigo-500 group-hover:bg-indigo-500/20', activeTile: 'border-indigo-500 bg-indigo-500 text-white shadow-sm' },
  { title: 'People', icon: Users, path: '/people', key: 'people',
    tile: 'border-sky-500/20 bg-sky-500/10 text-sky-500 group-hover:bg-sky-500/20', activeTile: 'border-sky-500 bg-sky-500 text-white shadow-sm' },
  { title: 'Work', icon: Briefcase, path: '/work', key: 'work',
    tile: 'border-amber-500/20 bg-amber-500/10 text-amber-500 group-hover:bg-amber-500/20', activeTile: 'border-amber-500 bg-amber-500 text-white shadow-sm' },
  { title: 'Money', icon: Wallet, path: '/money', key: 'money',
    tile: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500/20', activeTile: 'border-emerald-500 bg-emerald-500 text-white shadow-sm' },
  { title: 'Assets', icon: Package, path: '/assets', key: 'assets',
    tile: 'border-violet-500/20 bg-violet-500/10 text-violet-500 group-hover:bg-violet-500/20', activeTile: 'border-violet-500 bg-violet-500 text-white shadow-sm' },
  { title: 'Support', icon: Headset, path: '/support', key: 'support',
    tile: 'border-rose-500/20 bg-rose-500/10 text-rose-500 group-hover:bg-rose-500/20', activeTile: 'border-rose-500 bg-rose-500 text-white shadow-sm' },
  { title: 'Growth', icon: TrendingUp, path: '/growth', key: 'growth',
    tile: 'border-fuchsia-500/20 bg-fuchsia-500/10 text-fuchsia-500 group-hover:bg-fuchsia-500/20', activeTile: 'border-fuchsia-500 bg-fuchsia-500 text-white shadow-sm' },
  { title: 'Vault', icon: KeyRound, path: '/vault', key: 'vault',
    tile: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-500 group-hover:bg-cyan-500/20', activeTile: 'border-cyan-500 bg-cyan-500 text-white shadow-sm' },
  { title: 'Admin', icon: Settings, path: '/admin', key: 'admin',
    tile: 'border-teal-500/20 bg-teal-500/10 text-teal-500 group-hover:bg-teal-500/20', activeTile: 'border-teal-500 bg-teal-500 text-white shadow-sm' },
];

export function AppLauncher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Only show hubs this user may access. Dashboard (no key) is always shown;
  // Super Admin sees everything; an empty hub list (unknown / old backend)
  // falls back to People only.
  const hubs = useRbacStore((s) => s.hubs);
  const roles = useRbacStore((s) => s.roles);
  const canAccess = (key?: string) => {
    if (!key) return true;
    if (roles.includes('Super Admin')) return true;
    if (hubs.length === 0) return key === 'people';
    return hubs.includes(key);
  };
  const visibleApps = APPS.filter((app) => canAccess(app.key));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    const handleEscape = (event: KeyboardEvent) => event.key === 'Escape' && setIsOpen(false);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const isActive = (path: string) =>
    location.pathname === path ||
    location.pathname.startsWith(path + '/') ||
    (path === '/admin' && location.pathname.startsWith('/settings'));

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Switch app"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((o) => !o)}
      >
        <Grip />
      </Button>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-2 w-72 overflow-hidden rounded-lg border border-border bg-popover shadow-lg animate-scale-in">
          <div className="border-b border-border px-4 py-3">
            <h3 className="text-foreground font-display text-card-title">Your Apps</h3>
          </div>
          <div className="grid grid-cols-3 gap-1.5 p-3">
            {visibleApps.map((app) => {
              const Icon = app.icon;
              const active = isActive(app.path);
              return (
                <Button
                  variant="ghost"
                  size="unstyled"
                  key={app.title}
                  onClick={() => {
                    setIsOpen(false);
                    navigate(app.path);
                  }}
                  aria-current={active ? 'page' : undefined}
                  className="group h-auto w-full flex-col gap-2 rounded-xl p-2.5 transition-colors hover:bg-secondary/60"
                >
                  <span
                    className={cn(
                      'flex h-14 w-14 items-center justify-center rounded-xl border transition-all duration-150 group-hover:-translate-y-0.5 group-active:scale-95 [&_svg]:size-6',
                      active ? app.activeTile : app.tile
                    )}
                  >
                    <Icon strokeWidth={2} />
                  </span>
                  <span
                    className={cn(
                      'font-display text-menu transition-colors',
                      active ? 'font-semibold text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  >
                    {app.title}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
